"use client";

// ==============================================================================
// AuthProvider — Autenticación centralizada con TRS Auth
// ==============================================================================
// Maneja el flujo de login contra TRS Auth, almacena el JWT en localStorage,
// y provee funciones de autenticación a toda la aplicación.
// ==============================================================================

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";

// --- Tipos ---

interface AuthUser {
    id: string;
    username: string;
    display_name: string;
    services: string[];
}

interface AuthContextType {
    user: AuthUser | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    error: string | null;
}

// URL base de la API del clasificador — el backend tiene un proxy a TRS Auth
// El frontend llama a su propio backend (mismo dominio), evitando CORS.
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const SERVICE_ID = "finanzas"; // ID del servicio registrado en TRS Auth
const TOKEN_KEY = "trs_auth_token";
const USER_KEY = "trs_auth_user";

// --- Context ---

const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    login: async () => {},
    logout: () => {},
    error: null,
});

export function useAuth() {
    return useContext(AuthContext);
}

// --- Provider ---

export default function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Restaurar sesión desde localStorage al montar
    useEffect(() => {
        try {
            const savedToken = localStorage.getItem(TOKEN_KEY);
            const savedUser = localStorage.getItem(USER_KEY);
            if (savedToken && savedUser) {
                setToken(savedToken);
                setUser(JSON.parse(savedUser));
            }
        } catch {
            // localStorage no disponible o corrupto
        }
        setIsLoading(false);
    }, []);

    const login = useCallback(async (username: string, password: string) => {
        setError(null);
        setIsLoading(true);

        try {
            // Proxy via propio backend — evita CORS
            const res = await fetch(`${API_BASE}/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({ detail: "Error de autenticación" }));
                throw new Error(data.detail || data.error || data.message || "Credenciales incorrectas");
            }

            const data = await res.json();

            // TRS Auth puede pedir 2FA — manejar caso requiresTOTP
            if (data.requiresTOTP) {
                throw new Error("Se requiere código 2FA. Contactá al administrador.");
            }

            const jwt = data.token;
            // TRS Auth response: { token, refreshToken, user: { id, username, displayName, services: [{id, ...}] } }
            const serviceIds = (data.user?.services || []).map((s: any) =>
                typeof s === "string" ? s : s.id
            );
            const userData: AuthUser = {
                id: data.user?.id || "",
                username: data.user?.username || username,
                display_name: data.user?.displayName || data.user?.display_name || data.user?.username || username,
                services: serviceIds,
            };

            // Verificar que el usuario tiene acceso al servicio
            if (serviceIds.length > 0 && !serviceIds.includes(SERVICE_ID)) {
                throw new Error(`No tenés acceso al servicio "${SERVICE_ID}". Contactá al administrador.`);
            }

            // Guardar en estado y localStorage
            setToken(jwt);
            setUser(userData);
            localStorage.setItem(TOKEN_KEY, jwt);
            localStorage.setItem(USER_KEY, JSON.stringify(userData));

        } catch (e: any) {
            setError(e.message || "Error de autenticación");
            throw e;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    }, []);

    // --- Render ---

    // Mientras carga, mostrar pantalla de carga
    if (isLoading) {
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                background: "var(--bg-primary, #0a0a0a)",
                color: "var(--text-secondary, #888)",
                fontSize: "14px",
            }}>
                Cargando...
            </div>
        );
    }

    // Si no está autenticado, mostrar pantalla de login
    if (!token) {
        return <LoginScreen onLogin={login} error={error} />;
    }

    // Autenticado → renderizar hijos
    return (
        <AuthContext.Provider value={{ user, token, isAuthenticated: true, isLoading, login, logout, error }}>
            {children}
        </AuthContext.Provider>
    );
}


// --- Login Screen ---

function LoginScreen({ onLogin, error }: { onLogin: (u: string, p: string) => Promise<void>; error: string | null }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username.trim() || !password.trim()) return;

        setSubmitting(true);
        setLocalError(null);
        try {
            await onLogin(username, password);
        } catch (err: any) {
            setLocalError(err.message || "Error de autenticación");
        } finally {
            setSubmitting(false);
        }
    };

    const displayError = localError || error;

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            background: "var(--bg-primary, #0a0a0a)",
            padding: "20px",
        }}>
            <div style={{
                width: "100%",
                maxWidth: "400px",
                background: "var(--bg-card, #141414)",
                borderRadius: "var(--radius-lg, 12px)",
                border: "1px solid var(--border-subtle, #2a2a2a)",
                padding: "40px 32px",
                textAlign: "center",
            }}>
                {/* Logo */}
                <div style={{ fontSize: "48px", marginBottom: "8px" }}>🗂️</div>
                <h1 style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "var(--text-primary, #fff)",
                    marginBottom: "4px",
                }}>
                    Clasificador Administrativo
                </h1>
                <p style={{
                    fontSize: "13px",
                    color: "var(--text-tertiary, #666)",
                    marginBottom: "28px",
                }}>
                    Ingresá con tus credenciales de TRS Auth
                </p>

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    <input
                        type="text"
                        placeholder="Usuario"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoFocus
                        disabled={submitting}
                        style={{
                            width: "100%",
                            padding: "12px 14px",
                            borderRadius: "var(--radius-md, 8px)",
                            border: "1px solid var(--border-subtle, #2a2a2a)",
                            background: "var(--bg-secondary, #1a1a1a)",
                            color: "var(--text-primary, #fff)",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />
                    <input
                        type="password"
                        placeholder="Contraseña"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={submitting}
                        style={{
                            width: "100%",
                            padding: "12px 14px",
                            borderRadius: "var(--radius-md, 8px)",
                            border: "1px solid var(--border-subtle, #2a2a2a)",
                            background: "var(--bg-secondary, #1a1a1a)",
                            color: "var(--text-primary, #fff)",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />

                    {displayError && (
                        <div style={{
                            padding: "10px 14px",
                            borderRadius: "var(--radius-md, 8px)",
                            background: "rgba(239,68,68,0.1)",
                            border: "1px solid rgba(239,68,68,0.25)",
                            color: "#ef4444",
                            fontSize: "13px",
                            textAlign: "left",
                        }}>
                            {displayError}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || !username.trim() || !password.trim()}
                        style={{
                            width: "100%",
                            padding: "12px",
                            borderRadius: "var(--radius-md, 8px)",
                            background: submitting ? "#333" : "var(--accent-primary, #6366f1)",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "14px",
                            border: "none",
                            cursor: submitting ? "not-allowed" : "pointer",
                            opacity: submitting ? 0.7 : 1,
                            transition: "all 0.15s ease",
                        }}
                    >
                        {submitting ? "Ingresando..." : "Ingresar"}
                    </button>
                </form>

                <p style={{
                    fontSize: "11px",
                    color: "var(--text-tertiary, #555)",
                    marginTop: "20px",
                }}>
                    TRS Auth · Ecosistema The Rock Store
                </p>
            </div>
        </div>
    );
}
